import { format } from "date-fns";

import {
  CABIN_TYPES,
  CHILD_AGE_LIMITS,
  FLIGHT_LIMITS,
  TRIP_TYPES,
} from "@/feature/flight/search/types/constants";
import {
  DEFAULT_FLIGHT_FARE_TYPE,
  getPassengerTypeLabel as getFlightPassengerTypeLabel,
  normalizeFlightFareType,
} from "@/feature/flight/search/lib/passenger-types";
import { IAvailability, IFlightLeg } from "@/feature/flight/search/types/flight";
import {
  TAirport,
  TFlightSearchFormData,
} from "@/feature/flight/search/types/search";

const CABIN_TO_URL: Record<TFlightSearchFormData["cabin"], string> = {
  "1": "economy",
  "2": "premium",
  "3": "business",
  "4": "first",
};

const URL_TO_CABIN: Record<string, TFlightSearchFormData["cabin"]> = {
  economy: "1",
  premium: "2",
  business: "3",
  first: "4",
};

export const FlightParsers = {
  tripType(value: string | undefined): TFlightSearchFormData["tripType"] {
    if (!value) return "oneway";
    const v = value.toLowerCase().trim() as TFlightSearchFormData["tripType"];
    return TRIP_TYPES.some((t) => t.value === v) ? v : "oneway";
  },

  cabin(value: string | undefined): TFlightSearchFormData["cabin"] {
    if (!value) return "1";
    const v = value.toLowerCase().trim();
    if (URL_TO_CABIN[v]) return URL_TO_CABIN[v];
    return CABIN_TYPES.some((c) => c.value === v)
      ? (v as TFlightSearchFormData["cabin"])
      : "1";
  },

  fareType(value: string | undefined): TFlightSearchFormData["fareType"] {
    return normalizeFlightFareType(value);
  },

  passengers(value: string | undefined): TFlightSearchFormData["passengers"] {
    const defaults = { adults: 1, children: 0, infants: 0 };
    if (!value) return defaults;

    const parts = value.split("-").map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return defaults;

    const [adults, children, infants] = parts;
    const total = adults + children + infants;

    if (
      adults < FLIGHT_LIMITS.MIN_ADULTS ||
      children < 0 ||
      infants < 0 ||
      total > FLIGHT_LIMITS.MAX_PASSENGERS ||
      infants > adults
    ) {
      return defaults;
    }

    return { adults, children, infants };
  },

  flights(
    value: string | undefined,
  ): Array<{ from: TAirport; to: TAirport; departDate?: string }> {
    if (!value?.trim()) return [];
    const flights: Array<{ from: TAirport; to: TAirport; departDate?: string }> =
      [];

    const safeDecode = (text: string): string => {
      try {
        return decodeURIComponent(text);
      } catch {
        return text;
      }
    };

    const parseAirport = (str: string): TAirport => {
      const parts = str.split("~");
      const iata = parts[0] || "";
      const name = parts[1] || "";
      const city = parts[2] || "";
      const country = parts[3] || "";
      const lat = parts[4] || "";
      const lng = parts[5] || "";
      return {
        iata_code: iata.toUpperCase(),
        name: name ? safeDecode(name) : "",
        city_name: city ? safeDecode(city) : "",
        country: country ? safeDecode(country) : "",
        latitude: lat ? Number(lat) : undefined,
        longitude: lng ? Number(lng) : undefined,
      };
    };

    const splitRoute = (routePart: string): [string, string] | null => {
      // Delimit from->to by the "-" that starts the second airport IATA (e.g. "-JED~...").
      // This avoids breaking on hyphens inside airport names (e.g. "Airport-Rajshahi").
      for (let i = 0; i < routePart.length; i++) {
        if (routePart[i] !== "-") continue;

        const left = routePart.slice(0, i);
        const right = routePart.slice(i + 1);

        if (!/^[A-Z]{3}(?:~|$)/i.test(left)) continue;
        if (!/^[A-Z]{3}(?:~|$)/i.test(right)) continue;

        return [left, right];
      }
      return null;
    };

    const parseSegment = (
      segment: string,
    ): { from: TAirport; to: TAirport; departDate?: string; returnDate?: string } | null => {
      // Pull trailing "_yyyy-MM-dd" tokens from the right so "_" inside airport text is safe.
      let routePart = segment.trim();
      const dates: string[] = [];

      for (let i = 0; i < 2; i++) {
        const match = routePart.match(/_(\d{4}-\d{2}-\d{2})$/);
        if (!match) break;
        dates.unshift(match[1]);
        routePart = routePart.slice(0, -match[0].length);
      }

      const route = splitRoute(routePart);
      if (!route) return null;

      const fromAirport = parseAirport(route[0]);
      const toAirport = parseAirport(route[1]);

      if (
        !/^[A-Z]{3}$/i.test(fromAirport.iata_code) ||
        !/^[A-Z]{3}$/i.test(toAirport.iata_code)
      ) {
        return null;
      }
      if (fromAirport.iata_code === toAirport.iata_code) return null;

      return {
        from: fromAirport,
        to: toAirport,
        departDate: dates[0],
        returnDate: dates[1],
      };
    };

    for (const segment of value.split(",")) {
      const parsed = parseSegment(segment);
      if (!parsed) continue;

      const flight: { from: TAirport; to: TAirport; departDate?: string } = {
        from: parsed.from,
        to: parsed.to,
        departDate: parsed.departDate,
      };
      flights.push(flight);

      if (parsed.returnDate) {
        flights.push({
          from: parsed.to,
          to: parsed.from,
          departDate: parsed.returnDate,
        });
      }
    }
    return flights;
  },

  childrenAges(value: string | undefined): number[] {
    if (!value?.trim()) return [];
    return value
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter(
        (age) =>
          !isNaN(age) &&
          age >= CHILD_AGE_LIMITS.MIN &&
          age <= CHILD_AGE_LIMITS.MAX,
      );
  },
};

export const FlightSerializers = {
  cabin(cabin: TFlightSearchFormData["cabin"]): string {
    return CABIN_TO_URL[cabin] || "economy";
  },

  fareType(fareType: TFlightSearchFormData["fareType"]): string {
    return fareType || DEFAULT_FLIGHT_FARE_TYPE;
  },

  passengers(p: TFlightSearchFormData["passengers"]): string {
    return `${p.adults}-${p.children}-${p.infants}`;
  },

  flights(
    flights: Array<{
      from: TAirport;
      to: TAirport;
      departDate?: string | Date;
    }>,
  ): string {
    const encodeAirportText = (value: string): string =>
      encodeURIComponent(value)
        .replace(/-/g, "%2D")
        .replace(/_/g, "%5F")
        .replace(/~/g, "%7E");

    const serializeAirport = (airport: TAirport): string => {
      const parts = [
        airport.iata_code.toUpperCase(),
        airport.name ? encodeAirportText(airport.name) : "",
        airport.city_name ? encodeAirportText(airport.city_name) : "",
        airport.country ? encodeAirportText(airport.country) : "",
        airport.latitude != null ? String(airport.latitude) : "",
        airport.longitude != null ? String(airport.longitude) : "",
      ];
      while (parts.length > 1 && !parts[parts.length - 1]) {
        parts.pop();
      }
      return parts.join("~");
    };

    return flights
      .map((f) => {
        if (!f.from || !f.to) return null;

        const fromStr = serializeAirport(f.from);
        const toStr = serializeAirport(f.to);

        let segment = `${fromStr}-${toStr}`;

        if (f.departDate) {
          const date =
            f.departDate instanceof Date
              ? format(f.departDate, "yyyy-MM-dd")
              : f.departDate;
          segment += `_${date}`;
        }
        return segment;
      })
      .filter(Boolean)
      .join(",");
  },

  childrenAges(ages: (number | undefined)[]): string {
    return ages.filter((a): a is number => a != null).join(",");
  },
};

const airportCache = new Map<string, TAirport>();

export const AirportCache = {
  set(airport: TAirport) {
    if (airport.iata_code) {
      airportCache.set(airport.iata_code.toUpperCase(), airport);
    }
  },
  get(iataCode: string): TAirport {
    if (!iataCode)
      return { iata_code: "", name: "", city_name: "", country: "" };
    const cached = airportCache.get(iataCode.toUpperCase());
    return (
      cached || {
        iata_code: iataCode.toUpperCase(),
        name: "",
        city_name: "",
        country: "",
      }
    );
  },
  getAll() {
    return Object.fromEntries(airportCache);
  },
};

const getFlightDetailsTabName = (data?: IAvailability[]) => {
  if (!data) return null;
  const result = data.map(
    (item) => `${item.from_airport} - ${item.to_airport}`,
  );
  return result;
};

const getCabinClassLabel = (value: string) => {
  const cabinMap: Record<string, string> = {
    "1": "Economy",
    "2": "Premium Economy",
    "3": "Business",
    "4": "First Class",
  };
  return cabinMap[value] || "Economy";
};

const getTripTypeLabel = (value: string) => {
  const tripMap: Record<string, string> = {
    oneway: "One Way",
    roundtrip: "Round Trip",
    multicity: "Multi City",
  };
  return tripMap[value] || "Round Trip";
};

const getPassengerTypeLabel = (type: string): "Adult" | "Child" | "Infant" => {
  const label = getFlightPassengerTypeLabel(type);

  if (
    label === "Adult" ||
    label === "Student" ||
    label === "Umrah" ||
    label === "Senior Citizen"
  ) {
    return "Adult";
  }

  if (label === "Infant") return "Infant";
  if (label === "Child") return "Child";
  throw new Error(`Unknown passenger type: ${type}`);
};

const getFlightSegments = (flight: IFlightLeg) => {
  if (!flight?.options?.length) return { first: null, last: null };
  const first = flight.options[0];
  const last = flight.options[flight.options.length - 1];
  return { first, last };
};

export const flightHelper = {
  getFlightDetailsTabName,
  getPassengerTypeLabel,
  getCabinClassLabel,
  getTripTypeLabel,
  getFlightSegments,
};



